import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    date1: '',
    date2: '',
    date3: '',
    aiUsage: '',
    message: ''
  });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('mousemove', handleMouseMove);
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "EducationalOrganization",
      "name": "空間Lab AI研修",
      "description": "ChatGPT・Geminiを3時間のマンツーマン研修で実務レベルまで習得できるAI研修サービス",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "東京",
        "addressCountry": "JP"
      },
      "offers": {
        "@type": "Offer",
        "name": "AIマンツーマン研修（3時間）",
        "price": "280000",
        "priceCurrency": "JPY",
        "availability": "https://schema.org/LimitedAvailability",
        "validFrom": "2025-01-01"
      },
      "provider": {
        "@type": "Organization",
        "name": "株式会社 空間Lab",
        "url": "https://kukan-lab.com/"
      }
    });
    document.head.appendChild(schemaScript);

    // Add Course Schema
    const courseSchema = document.createElement('script');
    courseSchema.type = 'application/ld+json';
    courseSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "AIマンツーマン研修（3時間）",
      "description": "ChatGPT/Geminiを使った業務効率化、簡易アプリ生成まで学べる実践的なAI研修",
      "provider": {
        "@type": "Organization",
        "name": "株式会社 空間Lab",
        "url": "https://kukan-lab.com/"
      },
      "offers": {
        "@type": "Offer",
        "price": "280000",
        "priceCurrency": "JPY",
        "availability": "https://schema.org/LimitedAvailability"
      },
      "hasCourseInstance": {
        "@type": "CourseInstance",
        "courseMode": "online",
        "duration": "PT3H"
      }
    });
    document.head.appendChild(courseSchema);

    // Add FAQ Schema
    const faqSchema = document.createElement('script');
    faqSchema.type = 'application/ld+json';
    faqSchema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.a
        }
      }))
    });
    document.head.appendChild(faqSchema);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('mousemove', handleMouseMove);
      if (schemaScript.parentNode) schemaScript.parentNode.removeChild(schemaScript);
      if (courseSchema.parentNode) courseSchema.parentNode.removeChild(courseSchema);
      if (faqSchema.parentNode) faqSchema.parentNode.removeChild(faqSchema);
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const form = e.target as HTMLFormElement;
    const formDataToSend = new URLSearchParams();
    
    formDataToSend.append('name', formData.name);
    formDataToSend.append('company', formData.company);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('phone', formData.phone);
    formDataToSend.append('date1', formData.date1);
    formDataToSend.append('date2', formData.date2);
    formDataToSend.append('date3', formData.date3);
    formDataToSend.append('aiUsage', formData.aiUsage);
    formDataToSend.append('message', formData.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d56gnn69uelhi99933i0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setFormSubmitted(true);
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          date1: '',
          date2: '',
          date3: '',
          aiUsage: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const faqs = [
    {
      q: '初心者でも大丈夫ですか？',
      a: 'はい、問題ございません。ChatGPTやGeminiを少し触ったことがある方であれば、どなたでも受講いただけます。マンツーマンですので、お一人お一人のレベルに合わせて進めてまいります。'
    },
    {
      q: 'ChatGPTとGemini、どちらを使いますか？',
      a: '両方のツールを扱います。それぞれの特性や得意分野を理解し、業務に応じて使い分けられるようになることを目指します。'
    },
    {
      q: '事前準備は必要ですか？',
      a: 'ChatGPTまたはGeminiのアカウントをご用意ください。また、実際の業務課題をお持ちいただくと、より実践的な研修が可能です。詳細は無料相談時にご案内いたします。'
    },
    {
      q: '会社の機密情報を扱っても大丈夫ですか？',
      a: '機密情報の取り扱いについては、研修内で安全な使い方をお伝えします。また、秘密保持契約（NDA）の締結も可能ですので、ご相談ください。'
    },
    {
      q: 'オンラインと対面、どちらで受講できますか？',
      a: 'オンライン（Zoom等）での実施を基本としておりますが、対面をご希望の場合はご相談ください。場所や条件により対応可能です。'
    },
    {
      q: '領収書や請求書は発行できますか？',
      a: 'はい、発行可能です。お申し込み時にご希望をお伝えください。'
    },
    {
      q: '補講保証の条件を教えてください',
      a: '研修内容の理解が難しかった場合、後日1時間程度のフォローアップを無料で実施いたします。詳細な条件は個別にご案内いたします。'
    },
    {
      q: '支払い方法は何がありますか？',
      a: '銀行振込、クレジットカード決済に対応しております。詳細はお申し込み時にご案内いたします。'
    },
    {
      q: 'キャンセルポリシーについて教えてください',
      a: '開催日の7日前までは全額返金、3日前までは50%返金とさせていただいております。詳細は特定商取引法に基づく表記をご確認ください。'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 font-sans text-white overflow-hidden">
      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(14,165,233,0.1),transparent_50%)]"></div>
        <div 
          className="absolute w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl transition-all duration-1000 ease-out"
          style={{
            left: `${mousePosition.x - 192}px`,
            top: `${mousePosition.y - 192}px`,
          }}
        ></div>
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      {/* ヘッダー */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'bg-slate-950/80 backdrop-blur-xl border-b border-cyan-500/20 shadow-lg shadow-cyan-500/10' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                空間Lab AI研修
              </h1>
            </div>

            {/* デスクトップナビ */}
            <nav className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('problems')} className="text-gray-300 hover:text-cyan-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer hover:scale-105">
                悩み
              </button>
              <button onClick={() => scrollToSection('curriculum')} className="text-gray-300 hover:text-cyan-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer hover:scale-105">
                研修内容
              </button>
              <button onClick={() => scrollToSection('pricing')} className="text-gray-300 hover:text-cyan-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer hover:scale-105">
                料金
              </button>
              <button onClick={() => scrollToSection('benefits')} className="text-gray-300 hover:text-cyan-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer hover:scale-105">
                特典
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-gray-300 hover:text-cyan-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer hover:scale-105">
                実績・声
              </button>
              <button onClick={() => scrollToSection('faq')} className="text-gray-300 hover:text-cyan-400 transition-all duration-300 text-sm font-medium whitespace-nowrap cursor-pointer hover:scale-105">
                FAQ
              </button>
            </nav>

            <div className="hidden lg:block">
              <button onClick={() => scrollToSection('contact')} className="relative group bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-xl font-medium overflow-hidden transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-cyan-500/50 hover:scale-105">
                <span className="relative z-10">無料相談で日程確認</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>
            </div>

            {/* モバイルメニューボタン */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden text-gray-300 cursor-pointer">
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
            </button>
          </div>
        </div>

        {/* モバイルメニュー */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-t border-cyan-500/20">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('problems')} className="block w-full text-left text-gray-300 hover:text-cyan-400 py-2 cursor-pointer transition-colors">悩み</button>
              <button onClick={() => scrollToSection('curriculum')} className="block w-full text-left text-gray-300 hover:text-cyan-400 py-2 cursor-pointer transition-colors">研修内容</button>
              <button onClick={() => scrollToSection('pricing')} className="block w-full text-left text-gray-300 hover:text-cyan-400 py-2 cursor-pointer transition-colors">料金</button>
              <button onClick={() => scrollToSection('benefits')} className="block w-full text-left text-gray-300 hover:text-cyan-400 py-2 cursor-pointer transition-colors">特典</button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left text-gray-300 hover:text-cyan-400 py-2 cursor-pointer transition-colors">実績・声</button>
              <button onClick={() => scrollToSection('faq')} className="block w-full text-left text-gray-300 hover:text-cyan-400 py-2 cursor-pointer transition-colors">FAQ</button>
              <button onClick={() => scrollToSection('contact')} className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-xl font-medium transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-cyan-500/50">
                無料相談で日程確認
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ヒーローセクション */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                <i className="ri-star-fill mr-2 animate-pulse"></i>
                1日1組限定
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient">
                  AIの力を、
                </span>
                <br />
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent animate-gradient" style={{ animationDelay: '0.5s' }}>
                  3時間で引き出す。
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 leading-relaxed">
                ChatGPT/Geminiが<span className="text-cyan-400 font-semibold">"使える"</span>から<span className="text-cyan-400 font-semibold">"成果が出る"</span>状態へ。<br />
                マンツーマンで、簡易アプリ生成まで到達。
              </p>

              <div className="flex items-baseline gap-4">
                <span className="text-gray-500 line-through text-xl">通常 450,000円</span>
                <div className="flex items-baseline gap-2">
                  <span className="text-6xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">280,000</span>
                  <span className="text-3xl text-cyan-400">円</span>
                </div>
                <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-sm font-medium animate-pulse">先着20名限定</span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button onClick={() => scrollToSection('contact')} className="group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-xl font-medium overflow-hidden transition-all duration-300 text-lg whitespace-nowrap cursor-pointer hover:shadow-2xl hover:shadow-cyan-500/50 hover:scale-105">
                  <span className="relative z-10">無料相談で日程確認</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
                <button onClick={() => scrollToSection('contact')} className="bg-slate-800/50 backdrop-blur-sm text-cyan-400 border-2 border-cyan-500/50 px-8 py-4 rounded-xl font-medium hover:bg-slate-800 hover:border-cyan-400 transition-all duration-300 text-lg whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-105">
                  資料を受け取る
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8">
                <div className="flex items-start gap-3 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-time-line text-cyan-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-white">3時間完結</div>
                    <div className="text-sm text-gray-400">マンツーマン</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-shield-check-line text-cyan-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-white">補講保証</div>
                    <div className="text-sm text-gray-400">理解まで徹底</div>
                  </div>
                </div>
                <div className="flex items-start gap-3 group">
                  <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-gift-line text-cyan-400 text-xl"></i>
                  </div>
                  <div>
                    <div className="font-semibold text-white">特典200個</div>
                    <div className="text-sm text-gray-400">プロンプト集</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="relative z-10 group">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-3xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-500"></div>
                <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-cyan-500/20 group-hover:scale-105 transition-transform duration-500">
                  <iframe 
                    src="https://player.vimeo.com/video/1149422688?autoplay=1&loop=1&muted=1&background=1" 
                    className="w-full aspect-video"
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture"
                    title="AI研修イメージ"
                  ></iframe>
                </div>
              </div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-cyan-500/30 rounded-full blur-3xl animate-pulse"></div>
              <div className="absolute -bottom-6 -left-6 w-40 h-40 bg-blue-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* 共感セクション */}
      <section id="problems" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                こんなお悩み、ありませんか?
              </span>
            </h2>
            <p className="text-lg text-gray-400">
              多くの方が、AIの可能性を感じながらも、こんな壁にぶつかっています。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              'ChatGPT/Geminiを触るが、業務が変わらない',
              '指示が浅く、出力が薄い。期待した結果が得られない',
              '社内展開できない。再現性がなく属人化してしまう',
              '画像・資料作成に時間がかかり、結局手作業に戻る',
              '"結局自分でやった方が早い"という結論になっている',
              'どこから手をつければいいか分からず、活用が進まない'
            ].map((problem, index) => (
              <div key={index} className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-6 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-1 group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-check-line text-cyan-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-gray-200 font-medium">{problem}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 解決セクション */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                この研修で起きる変化
              </span>
            </h2>
            <p className="text-lg text-gray-400">
              AIの可能性を、体系的に引き出せるようになります。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-slate-900/50 backdrop-blur-sm border border-gray-700 rounded-3xl p-8 hover:scale-105 transition-transform duration-300">
              <div className="text-sm font-semibold text-gray-500 mb-3">BEFORE</div>
              <h3 className="text-3xl font-bold text-gray-300 mb-4">
                AIの力を<br />3〜10%しか<br />引き出せていない
              </h3>
              <ul className="space-y-3 text-gray-400">
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-1"></i>
                  <span>簡単な質問しかできない</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-1"></i>
                  <span>出力が浅く使えない</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-line text-red-400 mt-1"></i>
                  <span>業務に活かせていない</span>
                </li>
              </ul>
            </div>

            <div className="relative group bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-3xl p-8 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-sm font-semibold text-cyan-300 mb-3">AFTER</div>
                <h3 className="text-3xl font-bold text-white mb-4">
                  実務的に<br />50〜80%相当まで<br />引き出せる状態へ
                </h3>
                <ul className="space-y-3 text-gray-200">
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-cyan-400 mt-1"></i>
                    <span>的確な指示で高品質な出力</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-cyan-400 mt-1"></i>
                    <span>業務時間を大幅に短縮</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <i className="ri-check-line text-cyan-400 mt-1"></i>
                    <span>チーム全体で再現可能</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { icon: 'ri-file-text-line', title: '提案書作成', desc: '30分かかっていた提案書が、5分で完成。クオリティも向上。' },
              { icon: 'ri-book-open-line', title: 'マニュアル作成', desc: '社内マニュアルを自動生成。更新作業も効率化。' },
              { icon: 'ri-apps-line', title: '簡易アプリ作成', desc: '見積計算や議事録整形など、業務用ミニツールを自作。' }
            ].map((item, index) => (
              <div key={index} className="text-center group">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <i className={`${item.icon} text-cyan-400 text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 研修の特徴 */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                選ばれる5つの理由
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: 'ri-user-line', title: '3時間マンツーマンで最短到達', desc: 'お一人お一人のレベルと業務に合わせた完全個別指導。無駄なく、確実にスキルが身につきます。' },
              { icon: 'ri-lightbulb-line', title: '空間Labメソッド', desc: '型・テンプレート・チェックリストで体系化。研修後も忘れにくく、現場に定着します。' },
              { icon: 'ri-briefcase-line', title: '実案件を題材にする', desc: 'あなたの実際の業務課題を持ち込んで、その場で解決。即実践できる内容です。' },
              { icon: 'ri-shield-check-line', title: '補講保証', desc: '理解が難しかった部分は、後日無料でフォローアップ。安心して受講いただけます。' },
              { icon: 'ri-vip-crown-line', title: '1日1組限定', desc: '品質を担保するため、1日1組のみ。丁寧に、確実に成果を出すことにこだわります。' },
              { icon: 'ri-rocket-line', title: 'アプリ生成まで到達', desc: 'ChatGPT/Gemini内で簡易アプリを作成。業務用ミニツールを自分で作れるようになります。' }
            ].map((feature, index) => (
              <div key={index} className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-3xl p-8 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <i className={`${feature.icon} text-cyan-400 text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-400 leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* できるようになること */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                研修後、あなたができるようになること
              </span>
            </h2>
          </div>

          <div className="space-y-6 mb-12">
            {[
              { icon: 'ri-quill-pen-line', title: '良いプロンプトの作り方', desc: '目的→制約→評価の3ステップで、的確な指示を出せるようになります。' },
              { icon: 'ri-mail-line', title: '文章作成の効率化', desc: 'メール、提案書、議事録、要約など、あらゆる文章作成を高速化。' },
              { icon: 'ri-slideshow-line', title: '資料作成の自動化', desc: '構成案、スライド骨子、図解案を瞬時に生成。資料作成時間を大幅削減。' },
              { icon: 'ri-image-line', title: '画像生成の活用', desc: 'バナー、サムネイル、簡易広告など、ビジュアル制作も自分でできるように。' }
            ].map((skill, index) => (
              <div key={index} className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-6 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20 flex items-start gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <i className={`${skill.icon} text-cyan-400 text-xl`}></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-2">{skill.title}</h3>
                  <p className="text-gray-400">{skill.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="relative group bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-500/20 border border-cyan-500/30 rounded-3xl p-8 hover:scale-105 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/50 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-cyan-500/50">
                <i className="ri-code-box-line text-cyan-300 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-3">ChatGPT/Gemini内での簡易アプリ生成</h3>
                <p className="text-gray-200 mb-4 leading-relaxed">
                  見積計算、議事録整形、問い合わせ文自動生成など、業務用ミニツールを自分で作成できるようになります。プログラミング知識は不要です。
                </p>
                <div className="inline-flex items-center bg-cyan-500/20 border border-cyan-400/30 px-4 py-2 rounded-xl text-sm text-cyan-300">
                  <i className="ri-star-fill mr-2"></i>
                  研修のハイライト
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* カリキュラム */}
      <section id="curriculum" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                3時間の流れ
              </span>
            </h2>
            <p className="text-lg text-gray-400">
              無駄のない、成果にフォーカスしたカリキュラム
            </p>
          </div>

          <div className="relative">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500 via-blue-500 to-purple-500"></div>

            <div className="space-y-12">
              {[
                { time: '0:00\n0:20', title: '現状診断', desc: '業務の棚卸しとゴール設定。あなたの課題を明確にします。' },
                { time: '0:20\n1:10', title: 'AI基礎', desc: '仕組み、得意不得意、安全な使い方、社内ルールの設定方法を学びます。' },
                { time: '1:10\n2:10', title: '実務プロンプト実践', desc: '文章・資料・画像生成を実際に手を動かして習得。即実践できるスキルに。' },
                { time: '2:10\n2:50', title: 'アプリ生成', desc: '業務用ミニツールを作成。レベルに応じて最適化します。' },
                { time: '2:50\n3:00', title: '仕組み化', desc: 'テンプレート、再現手順、次の1週間の宿題を設定。継続的な成長をサポート。' }
              ].map((step, index) => (
                <div key={index} className="relative flex items-start gap-6 group">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0 relative z-10 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-cyan-500/50">
                    {step.time.split('\n')[0]}<br />{step.time.split('\n')[1]}
                  </div>
                  <div className="flex-1 pt-2 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl p-6 group-hover:border-cyan-500/50 group-hover:bg-slate-900/70 transition-all duration-300 hover:scale-105">
                    <h3 className="text-xl font-bold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 受講特典 */}
      <section id="benefits" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                受講生限定 特典
              </span>
            </h2>
            <p className="text-lg text-gray-400">
              研修後も、すぐに実践できる充実のサポート
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-3xl p-8 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                <i className="ri-image-2-line text-cyan-300 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">プロンプト100選</h3>
              <p className="text-gray-400 mb-4">画像・資料生成に特化したプロンプト集。コピー&ペーストですぐ使えます。</p>
              <div className="inline-flex items-center text-cyan-400 font-semibold">
                <i className="ri-gift-line mr-2"></i>
                特典1
              </div>
            </div>

            <div className="relative group bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-500/20 border border-cyan-500/30 rounded-3xl p-8 transform md:scale-105 hover:scale-110 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  <i className="ri-brain-line text-cyan-300 text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">プロンプト100選</h3>
                <p className="text-gray-200 mb-4">AIをさらに賢くする高度なプロンプト集。応用力が格段に上がります。</p>
                <div className="inline-flex items-center bg-cyan-500/20 border border-cyan-400/30 px-4 py-2 rounded-xl font-semibold text-sm text-cyan-300">
                  <i className="ri-star-fill mr-2"></i>
                  合計200個
                </div>
              </div>
            </div>

            <div className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-3xl p-8 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/20">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/30 to-blue-500/30 border border-cyan-400/50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                <i className="ri-file-list-line text-cyan-300 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">受講後テンプレ</h3>
              <p className="text-gray-400 mb-4">チェックリスト、社内展開用フォーマット。組織全体で活用できます。</p>
              <div className="inline-flex items-center text-cyan-400 font-semibold">
                <i className="ri-gift-line mr-2"></i>
                特典3
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 text-lg">
              受講後すぐに実践できる、実用的な特典をご用意しています。
            </p>
          </div>
        </div>
      </section>

      {/* 料金 */}
      <section id="pricing" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                料金プラン
              </span>
            </h2>
          </div>

          <div className="relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-red-500 to-pink-500 text-white px-6 py-2 rounded-full font-bold text-sm whitespace-nowrap z-10 animate-pulse">
              おすすめ
            </div>
            
            <div className="relative group bg-slate-900/50 backdrop-blur-sm border-4 border-cyan-500/50 rounded-3xl p-8 sm:p-12 hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/5 to-blue-500/5 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <div className="text-gray-500 text-lg mb-2">通常価格</div>
                  <div className="text-gray-600 line-through text-3xl mb-4">450,000円</div>
                  
                  <div className="mb-6">
                    <div className="text-6xl sm:text-7xl font-bold mb-2">
                      <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">280,000</span>
                      <span className="text-3xl text-cyan-400">円</span>
                    </div>
                    <div className="inline-flex items-center bg-gradient-to-r from-red-500 to-pink-500 text-white px-4 py-2 rounded-full font-bold animate-pulse">
                      先着20名限定
                    </div>
                  </div>

                  <div className="inline-flex items-center bg-cyan-500/20 border border-cyan-400/30 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-8">
                    <i className="ri-vip-crown-line mr-2"></i>
                    1日1組限定
                  </div>
                </div>

                <div className="space-y-4 mb-8">
                  {[
                    '3時間マンツーマン研修',
                    '補講保証（理解が難しい場合は後日フォロー）',
                    'プロンプト集200個（画像・資料・高度活用）',
                    '受講後テンプレート・チェックリスト',
                    '社内展開用フォーマット',
                    '簡易アプリ生成スキル習得'
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-cyan-400 text-xl mt-1 flex-shrink-0"></i>
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>

                <div className="text-center text-sm text-gray-500 mb-8">
                  お支払い方法：銀行振込、クレジットカード決済
                </div>

                <button onClick={() => scrollToSection('contact')} className="w-full group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-2xl hover:shadow-cyan-500/50 hover:scale-105">
                  <span className="relative z-10">無料相談で日程確認</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 安心材料 */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                安心して受講いただけます
              </span>
            </h2>
          </div>

          <div className="bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-3xl p-8 mb-12 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-cyan-400 text-xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">補講保証</h3>
                <p className="text-gray-400 leading-relaxed">
                  研修内容の理解が難しかった場合、後日1時間程度のフォローアップを無料で実施いたします。詳細な条件は個別にご案内いたします。安心してご受講ください。
                </p>
              </div>
            </div>
          </div>

          <div className="mb-12">
            <h3 className="text-2xl font-bold text-white text-center mb-8">お申し込み〜当日の流れ</h3>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { num: '1', title: '無料相談', desc: '現状をヒアリングし、研修内容をカスタマイズします。' },
                { num: '2', title: '日程確定', desc: 'ご希望の日程を調整し、お支払い方法をご案内します。' },
                { num: '3', title: '当日実施', desc: 'マンツーマンで3時間、集中して学びます。' }
              ].map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 shadow-lg shadow-cyan-500/50">
                    {step.num}
                  </div>
                  <h4 className="text-lg font-bold text-white mb-2">{step.title}</h4>
                  <p className="text-gray-400">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 実績・お客様の声 */}
      <section id="testimonials" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                導入実績・お客様の声
              </span>
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              様々な業種の方々に、実務で成果を出していただいています
            </p>
          </div>

          <div className="mb-12">
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              {['不動産業', '士業', '小売業', 'サロン', 'コンサルティング', '製造業', 'IT企業', '飲食業', '医療・介護', '教育'].map((industry, index) => (
                <span key={index} className="bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 text-gray-300 px-4 py-2 rounded-full text-sm font-medium hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105">
                  {industry}
                </span>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { name: '田中様', role: '不動産業 / 経営者', comment: '提案書作成が劇的に早くなりました。30分かかっていた作業が5分に。しかもクオリティも上がって、成約率も向上しています。' },
              { name: '佐藤様', role: '士業 / 個人事業主', comment: '簡易アプリ生成まで学べたのが大きかったです。見積計算ツールを自作でき、クライアントにも喜ばれています。' },
              { name: '鈴木様', role: '小売業 / マネージャー', comment: '社内マニュアルの作成・更新が自動生成できました。チーム全体で活用できるよう展開中です。投資対効果は抜群です。' },
              { name: '高橋様', role: 'サロン / オーナー', comment: 'SNS投稿の画像生成が自分でできるようになり、外注費が月5万円削減できました。デザインの幅も広がりました。' },
              { name: '伊藤様', role: 'コンサルティング / 代表', comment: 'クライアント向けレポート作成時間が3分の1に。空いた時間で新規案件に取り組めるようになり、売上が20%増加しました。' },
              { name: '渡辺様', role: '製造業 / 営業部長', comment: '営業資料のカスタマイズが効率化されました。わかりやすい説明文と図解案を瞬時に生成でき、教育の質も向上しています。' },
              { name: '小林様', role: '飲食業 / 店長', comment: 'メニュー説明文やPOP作成が簡単になりました。季節メニューの企画書も自分で作れるようになり、本部への提案がスムーズです。' },
              { name: '加藤様', role: '医療・介護 / 施設長', comment: 'スタッフ向けの研修資料作成が効率化されました。わかりやすい説明文と図解案を瞬時に生成でき、教育の質も向上しています。' },
              { name: '山本様', role: '教育 / 塾経営者', comment: '保護者向けの報告書作成が自動化でき、一人ひとりに合わせた内容を短時間で作成できます。保護者満足度も上がりました。' },
              { name: '吉田様', role: '不動産業 / 営業担当', comment: '物件紹介文の作成スピードが10倍になりました。魅力的な文章が書けるようになり、問い合わせ数が増加しています。' },
              { name: '松本様', role: '士業 / 税理士', comment: '顧問先への説明資料作成が簡単になりました。複雑な税制も分かりやすく説明でき、クライアントからの信頼が深まっています。' },
              { name: '中村様', role: 'IT企業 / プロジェクトマネージャー', comment: '議事録作成と要約が自動化され、会議後の作業時間が大幅に削減。チーム全体の生産性が向上しました。' }
            ].map((testimonial, index) => (
              <div key={index} className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-3xl p-8 hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/20">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <i className="ri-user-line text-cyan-400 text-2xl"></i>
                  </div>
                  <div>
                    <div className="font-bold text-white">{testimonial.name}</div>
                    <div className="text-sm text-gray-400">{testimonial.role}</div>
                  </div>
                </div>
                <p className="text-gray-300 leading-relaxed mb-4">
                  「{testimonial.comment}」
                </p>
                <div className="flex text-yellow-400">
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                  <i className="ri-star-fill"></i>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 text-lg">
              ※お客様の声は実際の受講生の感想を基に作成しています
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                よくあるご質問
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="group bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-2xl overflow-hidden hover:border-cyan-500/50 hover:bg-slate-900/70 transition-all duration-300">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left transition-colors cursor-pointer"
                >
                  <div className="flex items-start gap-4 flex-1">
                    <span className="text-cyan-400 font-bold flex-shrink-0">Q</span>
                    <span className="font-semibold text-white">{faq.q}</span>
                  </div>
                  <i className={`ri-arrow-down-s-line text-gray-400 text-xl transition-transform flex-shrink-0 ${openFaq === index ? 'transform rotate-180' : ''}`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 border-l-4 border-cyan-500 ml-6 animate-fade-in">
                    <div className="flex items-start gap-4">
                      <span className="text-gray-500 font-bold flex-shrink-0">A</span>
                      <p className="text-gray-300 leading-relaxed">{faq.a}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 最終CTA */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="relative group bg-gradient-to-br from-cyan-500/20 via-blue-500/20 to-purple-500/20 border border-cyan-500/30 rounded-3xl p-12 hover:border-cyan-400 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/30">
            <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                  3時間で、AIが<br className="sm:hidden" />"現場で使える武器"になる。
                </span>
              </h2>
              
              <p className="text-xl text-gray-300 mb-8">
                今すぐ無料相談で、あなたの課題をお聞かせください。
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <button onClick={() => scrollToSection('contact')} className="group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-8 py-4 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-2xl hover:shadow-cyan-500/50 hover:scale-105">
                  <span className="relative z-10">無料相談で日程確認</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </button>
                <button onClick={() => scrollToSection('contact')} className="bg-slate-800/50 backdrop-blur-sm text-cyan-400 border-2 border-cyan-500/50 px-8 py-4 rounded-2xl font-bold text-lg hover:bg-slate-800 hover:border-cyan-400 transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-cyan-500/30 hover:scale-105">
                  資料を受け取る
                </button>
              </div>

              <div className="inline-flex items-center bg-cyan-500/20 border border-cyan-400/30 px-6 py-3 rounded-full text-cyan-300">
                <i className="ri-time-line mr-2"></i>
                <span className="font-semibold">先着20名 280,000円</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* お問い合わせフォーム */}
      <section id="contact" className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                無料相談 お申し込み
              </span>
            </h2>
            <p className="text-lg text-gray-400">
              まずはお気軽にご相談ください。24時間以内にご返信いたします。
            </p>
          </div>

          {formSubmitted ? (
            <div className="bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500/50 rounded-3xl p-8 text-center backdrop-blur-sm">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-cyan-500/50">
                <i className="ri-check-line text-white text-3xl"></i>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">送信完了</h3>
              <p className="text-gray-300">
                お問い合わせありがとうございます。<br />
                24時間以内に担当者よりご連絡させていただきます。
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-readdy-form>
              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  お名前 <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="山田 太郎"
                />
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  会社名（任意）
                </label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  メールアドレス <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="example@email.com"
                />
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  電話番号（任意）
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  placeholder="090-1234-5678"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">
                    希望日程 第1希望
                  </label>
                  <input
                    type="date"
                    name="date1"
                    value={formData.date1}
                    onChange={(e) => setFormData({...formData, date1: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">
                    第2希望
                  </label>
                  <input
                    type="date"
                    name="date2"
                    value={formData.date2}
                    onChange={(e) => setFormData({...formData, date2: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">
                    第3希望
                  </label>
                  <input
                    type="date"
                    name="date3"
                    value={formData.date3}
                    onChange={(e) => setFormData({...formData, date3: e.target.value})}
                    className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  現在のAI利用状況
                </label>
                <select
                  name="aiUsage"
                  value={formData.aiUsage}
                  onChange={(e) => setFormData({...formData, aiUsage: e.target.value})}
                  className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="">選択してください</option>
                  <option value="未使用">まだ使ったことがない</option>
                  <option value="初心者">少し触ったことがある</option>
                  <option value="中級者">日常的に使っている</option>
                  <option value="上級者">業務で活用している</option>
                </select>
              </div>

              <div>
                <label className="block text-gray-300 font-semibold mb-2">
                  相談内容・ご質問
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setFormData({...formData, message: e.target.value});
                    }
                  }}
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-900/50 backdrop-blur-sm border border-cyan-500/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-none transition-all duration-300"
                  placeholder="具体的な課題や、研修で学びたいことをお聞かせください（500文字以内）"
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length} / 500
                </div>
              </div>

              <button
                type="submit"
                className="w-full group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4 rounded-2xl font-bold text-lg overflow-hidden transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-2xl hover:shadow-cyan-500/50 hover:scale-105"
              >
                <span className="relative z-10">送信する</span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </button>

              <p className="text-center text-sm text-gray-400">
                <i className="ri-time-line mr-1"></i>
                24時間以内にご返信いたします
              </p>
            </form>
          )}
        </div>
      </section>

      {/* フッター */}
      <footer className="relative bg-slate-950 border-t border-cyan-500/20 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent mb-4">
                空間Lab
              </div>
              <p className="text-gray-400 text-sm">
                株式会社 空間Lab<br />
                AIで、あなたの仕事を変える。
              </p>
            </div>

            <div>
              <h3 className="font-bold text-white mb-4">リンク</h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="https://kukan-lab.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors">
                    会社情報
                  </a>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-400 hover:text-cyan-400 transition-colors">
                    プライバシーポリシー
                  </Link>
                </li>
                <li>
                  <Link to="/legal" className="text-gray-400 hover:text-cyan-400 transition-colors">
                    特定商取引法に基づく表記
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="font-bold text-white mb-4">お問い合わせ</h3>
              <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-cyan-400 transition-colors text-sm cursor-pointer">
                無料相談フォーム
              </button>
            </div>
          </div>

          <div className="border-t border-cyan-500/20 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              &copy; 2025 株式会社 空間Lab. All rights reserved.
            </p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-cyan-400 transition-colors text-sm">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      {/* モバイル固定CTA */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-slate-950/95 backdrop-blur-xl border-t border-cyan-500/20 p-4 z-40 shadow-lg shadow-cyan-500/20">
        <button onClick={() => scrollToSection('contact')} className="w-full group relative bg-gradient-to-r from-cyan-500 to-blue-500 text-white py-4 rounded-xl font-bold overflow-hidden transition-all duration-300 whitespace-nowrap cursor-pointer hover:shadow-lg hover:shadow-cyan-500/50">
          <span className="relative z-10">無料相談で日程確認</span>
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        </button>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out;
        }
      `}</style>
    </div>
  );
}
